import * as React from 'react';
import {SystemIcons} from '../icons';
import {COLORS} from '../styles';
import {Size, States, Testable} from '../types';
import {InputFieldStyling, InputWrapper, NoteMessage, ValidationMessage} from './styling';
import {useFocusVisibleRef} from '../common';
import {TextFieldNote} from './types';
import TooltipOverflow from '../Tooltips/TooltipOverflow';

export type TextFieldProps =
  Testable & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'disabled' | 'id'  | 'type' | 'readOnly' | 'autoComplete' | 'pattern' | 'maxLength'>
  & {
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (text: string) => void;
  value?: string;
  validationMessage?: string;
  type?: string;
  state?: States.Invalid | States.Valid;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  pattern?: string;
  maxLength?: number;
  withoutBorder?: boolean;
  overflowTooltipPosition?: 'top' | 'bottom';
  size?: Size.Small | Size.Medium;
  margin?: string;
  note?: TextFieldNote;
};

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(({
                                      id,
                                      disabled,
                                      readOnly,
                                      onChange,
                                      value,
                                      validationMessage,
                                      type,
                                      autoComplete,
                                      placeholder,
                                      required,
                                      pattern,
                                      maxLength,
                                      withoutBorder,
                                      state,
                                      size,
                                      margin,
                                      overflowTooltipPosition,
                                      note,
                                      className,
                                      dataTestId,
                                      ...rest
                                    }: TextFieldProps, ref) => {
  const elementRef = useFocusVisibleRef();

  React.useImperativeHandle(ref, () => elementRef.current, [elementRef]);

  const cls = `${state || (validationMessage ? States.Invalid : '')} ${size || ''}`;

  return (
    <>
      <InputWrapper
        readOnly={readOnly}
        disabled={disabled}
        margin={margin}
        onClick={(e: any) => {
          if (disabled || readOnly) {
            e.preventDefault();
          }
        }}>
        <InputFieldStyling
          id={id}
          data-testid={dataTestId}
          ref={elementRef}
          type={type || 'text'}
          value={value}
          className={cls}
          tabIndex={readOnly || disabled ? -1 : 0}
          autoComplete={autoComplete}
          placeholder={disabled ? undefined : placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(e: any) => onChange && onChange(e?.target?.value || '')}
          activeErrorMessage={!!validationMessage && state !== States.Valid}
          required={required}
          pattern={pattern}
          maxLength={maxLength}
          withoutBorder={withoutBorder}
          {...rest}
        />
        <TooltipOverflow value={value} position={overflowTooltipPosition} input={elementRef} withArrow={true} maxWidth="100%" size={size} align='end'>
          {value}
        </TooltipOverflow>

        {validationMessage && (
          <ValidationMessage className={size || ''} type={state ?? States.Invalid}>
            {state === States.Valid ? <SystemIcons.CheckMark color={COLORS.correct_400}/> :
              <SystemIcons.TechnicalWarning color={COLORS.critical_400}/>}
            <span>{validationMessage}</span>
          </ValidationMessage>
        )}
        {note && !disabled && (
          <NoteMessage className={size}>
            {note.icon}
            <span>{note.message}</span>
          </NoteMessage>
        )}
      </InputWrapper>
    </>
  );
});

export default TextField;
